import { useEffect } from "react";
import { isEqual } from "lodash";
import qs from "qs";
import { z } from "zod";

import {
  convertFrameworkRelationshipFilterToFrameworkFilterOptions,
  DEFAULT_FILTERS,
  DEFAULT_NOTICE_COLUMNS,
  DEFAULT_NOTICE_SORT_ORDER,
  EMPTY_FILTERS,
  getAppliedFilters,
  NoticeColumns,
  NoticeColumnsSchema,
  NoticeFilters,
  replaceStaticItemsWithTokens,
} from "components/notices/utils";
import {
  paramsToDateRangeObj,
  paramsToNullableDateRangeObj,
  paramsToNumericRangeObj,
} from "components/record_search/types";
import { useFetchBuyerIdentifiers } from "lib/hooks/api/buyer/useBuyerIdentifiers";
import { PinnedView } from "lib/hooks/api/notices/views/usePinnedViews";
import { SignalSettings, useSignalSettings } from "lib/hooks/api/teams/useSignalSettings";
import { useLocalStorage } from "lib/hooks/useLocalStorage";
import { parseBooleanValue, parseNumberValue, useURLState } from "lib/hooks/useURLState";
import { routes } from "lib/routes";
import { createSavedViewSelectedEvent } from "lib/savedViewTracking";
import { urlParamToArray } from "lib/search/url_params";
import { EventNames, useTracking } from "lib/tracking";
import { NoticesStagesv2, SavedViewDataType, SupplierMentionType } from "lib/types/graphQLEnums";
import {
  ALL_COMPETITORS_TOKEN,
  ALL_CPV_CODES_TOKEN,
  ALL_KEYWORDS_TOKEN,
  ALL_PARTNERS_TOKEN,
  Status,
} from "lib/types/models";
import { SavedViewType } from "lib/types/savedViews";
import { simpleArrayDedupe } from "lib/utils";
import { useAllViews } from "../../lib/hooks/api/notices/views/useAllViews";
import { usePinnedViews } from "../../lib/hooks/api/notices/views/usePinnedViews";

let lastSetRecentViewId: string | null = null; // Module level on purpose

export function convertViewToNoticeFilters({
  savedView,
  signalSettings,
}: {
  savedView: SavedViewType;
  signalSettings?: SignalSettings;
}): NoticeFilters {
  const { view } = savedView;

  let relevanceScore = view.filterSettings.relevanceScore || [];
  const supplierGuids = view.filterSettings.suppliers?.guid || [];
  const cpvCodes = view.filterSettings.cpvCodes || [];
  let keywords = view.filterSettings.keywords || [];

  if (signalSettings) {
    convertSignalTokensToFilters(supplierGuids, cpvCodes, keywords, signalSettings);
  }

  const noticeLists =
    view.filterSettings.noticeLists?.map((noticeList) => noticeList.toString()) || [];

  //TODO: remove this after migrating saved views to use noticeLists instead of noticeList
  if (!view.filterSettings.noticeLists && view.filterSettings.noticeList) {
    noticeLists?.push(view.filterSettings.noticeList.toString());
  }

  const includeHiddenNotices = view.filterSettings.includeHiddenNotices ?? false;

  return {
    signals: [],
    assignee: view.filterSettings.assignee || [],
    buyerListIds: view.filterSettings.buyerListIds || [],
    buyers: view.filterSettings.buyerGuids || [],
    buyerCategories: view.filterSettings.buyerCategoryId || [],
    buyerCountryRegions: {
      countries: view.filterSettings.buyerCountryCodes || [],
      regions: view.filterSettings.buyerRegionIds || [],
      selected: [
        ...(view.filterSettings.buyerCountryCodes ?? []),
        ...(view.filterSettings.buyerRegionIds ?? []),
      ],
    },
    value: {
      to: view.filterSettings.value?.to || undefined,
      from: view.filterSettings.value?.from || undefined,
      hideNulls:
        view.filterSettings.valueNulls === undefined ? false : !view.filterSettings.valueNulls,
    },
    text: view.filterSettings.text || "",
    stage:
      // TODO: Remove once we migrate saved views to the new stages (procurement-act)
      convertStagesForProcurementAct(view.filterSettings.stage as NoticeFilters["stage"]) || [],
    relevanceScore,
    awardDate: {
      filter: {
        ...view.filterSettings.awardDate,
        to: view.filterSettings.awardDate?.to,
        from: view.filterSettings.awardDate?.from,
      },
      hideNulls:
        view.filterSettings.awardDate === undefined ? false : !view.filterSettings.awardDate,
    },
    closeDate: {
      filter: {
        ...view.filterSettings.closeDate,
        to: view.filterSettings.closeDate?.to,
        from: view.filterSettings.closeDate?.from,
      },
      hideNulls:
        view.filterSettings.closeNulls === undefined ? false : !view.filterSettings.closeNulls,
    },
    expiryDate: {
      filter: {
        ...view.filterSettings.expiryDate,
        to: view.filterSettings.expiryDate?.to,
        from: view.filterSettings.expiryDate?.from,
      },
      hideNulls:
        view.filterSettings.expiryNulls === undefined ? false : !view.filterSettings.expiryNulls,
    },
    publishDate: {
      ...view.filterSettings.publishDate,
      to: view.filterSettings.publishDate?.to,
      from: view.filterSettings.publishDate?.from,
    },
    sort: {
      field: view.sortOrder?.field || DEFAULT_NOTICE_SORT_ORDER.field,
      order:
        (view.sortOrder?.order as NoticeFilters["sort"]["order"]) ||
        DEFAULT_NOTICE_SORT_ORDER.order,
    },
    suppliers: view.filterSettings.suppliers?.id || [],
    supplierGuids: simpleArrayDedupe(supplierGuids),
    supplierSme: view.filterSettings.suppliers?.isSme ?? "",
    noticeLists: noticeLists || [],
    frameworkIds: view.filterSettings.frameworks?.ids || [],
    frameworkFilterOptions:
      convertFrameworkRelationshipFilterToFrameworkFilterOptions(
        view.filterSettings.frameworks?.frameworkRelationship,
      ) || "",
    supplierMentionType:
      (view.filterSettings.suppliers?.mentionType as SupplierMentionType) || undefined,
    keywords: simpleArrayDedupe(keywords),
    excludeKeywords: view.filterSettings.excludeKeywords || [],
    cpvCodes: simpleArrayDedupe(cpvCodes),
    ...(includeHiddenNotices ? { includeHiddenNotices } : {}),
  };
}

function convertSignalTokensToFilters(
  supplierGuids: string[],
  cpvCodes: string[],
  keywords: string[],
  signalSettings: SignalSettings,
) {
  const {
    competitors,
    partners,
    cpvCodes: cpvCodeSignals,
    keywords: keywordSignals,
  } = signalSettings;
  // Add all competitors and partners to the guids if the token is present
  if (supplierGuids.includes(ALL_COMPETITORS_TOKEN) && competitors && competitors.length > 0) {
    supplierGuids.push(...competitors.map((c) => c.id));
  }
  if (supplierGuids.includes(ALL_PARTNERS_TOKEN) && partners && partners.length > 0) {
    supplierGuids.push(...partners.map((p) => p.id));
  }

  if (cpvCodes.includes(ALL_CPV_CODES_TOKEN) && cpvCodeSignals && cpvCodeSignals.length > 0) {
    cpvCodes.push(...cpvCodeSignals);
  }
  if (keywords.includes(ALL_KEYWORDS_TOKEN) && keywordSignals && keywordSignals.length > 0) {
    keywords.push(...keywordSignals);
  }
}

type FiltersArrayKeys = keyof Pick<NoticeFilters, "signals" | "assignee">;

type FiltersObjectKeys = keyof Pick<NoticeFilters, "publishDate">;

function convertStagesForProcurementAct(currentStages?: (Status | NoticesStagesv2)[]) {
  // TODO: Remove this logic once we have migrated all views to the new stage values (procurement-act)
  const oldStages = ["OPEN", "CLOSED", "AWARDED", "PRE_TENDER", "STALE_PRE_TENDER"];

  if (!currentStages?.some((s) => oldStages.includes(s))) {
    return currentStages;
  }
  const newStages = currentStages.filter((s) => !oldStages.includes(s)) as NoticesStagesv2[];

  // https://www.notion.so/stotles/Stage-definition-work-184b27cc426d805fb1c1c581114f6b00?pvs=4#18fb27cc426d802cbf9ed4d33313a3d3
  if (currentStages.includes("PRE_TENDER")) {
    newStages.push(
      NoticesStagesv2.Pipeline,
      NoticesStagesv2.CancelledPreTender,
      NoticesStagesv2.PreTenderV2,
    );
  }
  // Cancelled pre-tenders derived STALE_PRE_TENDER_V2 before the stage existed, so views saved
  // against the old stage set still need to match them.
  if (currentStages.includes("STALE_PRE_TENDER")) {
    newStages.push(NoticesStagesv2.StalePreTenderV2, NoticesStagesv2.CancelledPreTender);
  }
  if (currentStages.includes("OPEN")) {
    newStages.push(NoticesStagesv2.OpenTender);
  }
  if (currentStages.includes("CLOSED")) {
    newStages.push(NoticesStagesv2.ClosedTender, NoticesStagesv2.CancelledTender);
  }
  if (currentStages.includes("AWARDED")) {
    newStages.push(
      NoticesStagesv2.AwardedContract,
      NoticesStagesv2.ExpiredContract,
      NoticesStagesv2.CancelledAward,
    );
  }
  return newStages;
}

function expandTokens(existingFieldContent: string[], token: string, tokenValues: string[]) {
  if (!existingFieldContent.includes(token)) {
    return existingFieldContent;
  }
  const expanded = [...existingFieldContent, ...tokenValues];
  return simpleArrayDedupe(expanded);
}

export function parseUrlState(
  val?: NoticeFilters,
  defaultFilters?: NoticeFilters,
  signalSettings?: SignalSettings | null,
) {
  if (!val) {
    return defaultFilters || DEFAULT_FILTERS;
  }

  // if array values are undefined set as an empty array otherwise default will override
  Object.keys(DEFAULT_FILTERS).forEach((k) => {
    const key = k as keyof NoticeFilters;
    if (
      typeof DEFAULT_FILTERS[key] === "object" &&
      !Array.isArray(DEFAULT_FILTERS[key]) &&
      !val[key]
    ) {
      val[key as FiltersObjectKeys] = {};
    }
    if (Array.isArray(DEFAULT_FILTERS[key]) && !val[key]) {
      val[key as FiltersArrayKeys] = [];
    }
  });

  const currentStages = convertStagesForProcurementAct(val.stage);

  return {
    ...defaultFilters,
    ...val,
    stage: currentStages || [],
    value: {
      from: parseNumberValue(val.value?.from),
      to: parseNumberValue(val.value?.to),
      hideNulls: parseBooleanValue(val.value?.hideNulls),
    },
    awardDate: {
      filter: val.awardDate?.filter,
      hideNulls: parseBooleanValue(val.awardDate?.hideNulls),
    },
    closeDate: {
      filter: val.closeDate?.filter,
      hideNulls: parseBooleanValue(val.closeDate?.hideNulls),
    },
    expiryDate: {
      filter: val.expiryDate?.filter,
      hideNulls: parseBooleanValue(val.expiryDate?.hideNulls),
    },
    cpvCodes: expandTokens(
      val.cpvCodes ?? defaultFilters?.cpvCodes,
      ALL_CPV_CODES_TOKEN,
      signalSettings?.cpvCodes ?? [],
    ),
    keywords: expandTokens(
      val.keywords ?? defaultFilters?.keywords,
      ALL_KEYWORDS_TOKEN,
      signalSettings?.keywords ?? [],
    ),
    suppliers: val.suppliers.map((id) => Number(id)) || [],
    supplierGuids: expandTokens(
      expandTokens(
        val.supplierGuids ?? [],
        ALL_COMPETITORS_TOKEN,
        signalSettings?.competitors?.map((o) => o.id) ?? [],
      ),
      ALL_PARTNERS_TOKEN,
      signalSettings?.partners?.map((o) => o.id) ?? [],
    ),
    supplierSme: parseBooleanValue(val.supplierSme) ?? "",
    includeHiddenNotices: parseBooleanValue(val.includeHiddenNotices),
    sort: {
      field: val.sort?.field || defaultFilters?.sort?.field || DEFAULT_NOTICE_SORT_ORDER.field,
      order: val.sort?.order || defaultFilters?.sort?.field || DEFAULT_NOTICE_SORT_ORDER.order,
    },
  };
}

function parseOldUrl() {
  const urlParams = new URLSearchParams(window.location.search);
  const currentView = urlParams.get("current_view_id");
  const currentPage = urlParams.get("page");
  const pageSize = urlParams.get("page_size");

  const filters: Partial<NoticeFilters> = {};

  filters.text = urlParams.get("text") || undefined;
  filters.buyers = urlParamToArray(urlParams, "buyer_guids");
  filters.stage = urlParamToArray(urlParams, "stage")?.map((s) => s.toUpperCase()) as Status[];
  const closeDate = paramsToNullableDateRangeObj(urlParams, "close_date");
  if (closeDate) {
    filters.closeDate = {
      filter: {
        ...closeDate.filter,
        from: closeDate.filter?.from?.toISOString(),
        to: closeDate.filter?.to?.toISOString(),
      },
      hideNulls: !closeDate.includeNulls,
    };
  }
  const expiryDate = paramsToNullableDateRangeObj(urlParams, "expiry_date");
  if (expiryDate) {
    filters.expiryDate = {
      filter: {
        ...expiryDate.filter,
        from: expiryDate.filter?.from?.toISOString(),
        to: expiryDate.filter?.to?.toISOString(),
      },
      hideNulls: !expiryDate.includeNulls,
    };
  }
  const publishDate = paramsToDateRangeObj(urlParams, "publish_date");
  if (publishDate) {
    filters.publishDate = {
      ...publishDate,
      from: publishDate.from?.toISOString(),
      to: publishDate.to?.toISOString(),
    };
  }
  const signalIds = urlParamToArray(urlParams, "any_signals_ids");
  const signalCategories = urlParamToArray(urlParams, "any_signals_categories");
  if (signalIds || signalCategories) {
    filters.signals = [...(signalIds || []), ...(signalCategories || [])];
  }
  filters.buyerCategories = urlParamToArray(urlParams, "buyer_category_id");
  const value = paramsToNumericRangeObj(urlParams, "value");
  if (value) {
    filters.value = {
      from: value.from,
      to: value.to,
      hideNulls: !value.includeNulls,
    };
  }
  filters.assignee = urlParamToArray(urlParams, "assignee");
  filters.buyerListIds = urlParamToArray(urlParams, "buyer_list_ids");

  const sortField = urlParams.get("sort");
  const sortOrder = urlParams.get("sort_order");
  if (sortField || sortOrder) {
    filters.sort = {
      field: sortField || "publish_date",
      order: (sortOrder as NoticeFilters["sort"]["order"]) || "desc",
    };
  }

  const params = {
    noticeFilters: isEqual(filters, {}) ? undefined : filters,
    current_view_id: currentView,
    page: currentPage,
    page_size: pageSize,
  };

  // only check filters as the other params are handled by the useURLState hook
  if (isEqual(filters, {}) || Object.values(filters).every((v) => !v)) {
    return undefined;
  }

  // signal were hidden by default so if they are not set then set them to the default
  if (!filters.signals) {
    filters.signals = DEFAULT_FILTERS.signals;
  }

  return params;
}

type MyFeedViewsResponse = {
  pinnedViews: PinnedView[] | undefined;
  allViews: SavedViewType[] | undefined;
  selectedView: SavedViewType | undefined;
  isLoadingViews: boolean;
  recentViews: string[];
  setUrlViewId: (newValue: string | undefined) => void;
  setRecentViews: (newValue: string[]) => void;
  setShowNoView: (newValue: boolean | undefined) => void;
  showNoView: boolean | undefined;
  urlViewId: string | undefined;
};

export function useSavedViews(isEnabled: boolean = true): MyFeedViewsResponse {
  const { data: pinnedViews, isLoading: isLoadingPinned } = usePinnedViews(
    SavedViewDataType.Notice,
    { enabled: isEnabled },
  );

  const { data: allViews, isLoading: isLoadingAllViews } = useAllViews(SavedViewDataType.Notice, {
    enabled: isEnabled,
  });

  const defaultView =
    allViews?.find((v) => v.id === pinnedViews?.[0]?.savedViewId) || allViews?.[0];

  const isLoadingViews = isLoadingPinned || isLoadingAllViews;

  const [urlViewId, setUrlViewId] = useURLState(
    "current_view_id",
    undefined,
    z.string().optional(),
  );
  const urlView = allViews?.find((v) => v.id === urlViewId);

  const [recentViews, setRecentViews] = useLocalStorage<string[]>("RECENT_VIEWS", []);
  const recentView = recentViews[0] ? allViews?.find((v) => v.id === recentViews[0]) : undefined;

  // used to determine when a user purposely deselects a view
  const [showNoView, setShowNoView] = useURLState<boolean | undefined>(
    "showNoView",
    false,
    parseBooleanValue,
  );

  const selectedView = showNoView ? undefined : urlView || recentView || defaultView;

  useEffect(() => {
    if (
      isEnabled &&
      urlViewId &&
      !showNoView &&
      recentViews[0] !== urlViewId &&
      lastSetRecentViewId !== urlViewId
    ) {
      // Module level variable to avoid the recent view being set multiple times due to multiple hook instances
      // and to prevent infinite loops from occurring if a bug is introduced that causes an infinite loop
      lastSetRecentViewId = urlViewId;

      // Add view to recent views or moves it to the top if already exists
      setRecentViews([urlViewId, ...recentViews.filter((v) => v !== urlViewId)]);
    }
  }, [isEnabled, urlViewId, showNoView, recentViews, setRecentViews]);

  return {
    pinnedViews,
    allViews,
    selectedView,
    isLoadingViews,
    recentViews,
    setRecentViews,
    urlViewId,
    setUrlViewId,
    showNoView,
    setShowNoView,
  };
}

export function useNoticeListsPageState(isEnabled: boolean = true) {
  const { logEvent } = useTracking();

  useEffect(() => {
    if (!isEnabled) return;

    const convertedParams = parseOldUrl();
    if (convertedParams) {
      const newQsValue = qs.stringify(convertedParams, { arrayFormat: "brackets" });
      window.location.assign(`${routes.notices}?${newQsValue}`);
    }
  }, [isEnabled]);

  const { data: signals, isLoading: isLoadingSignals } = useSignalSettings(undefined, {
    enabled: isEnabled,
  });

  const {
    pinnedViews,
    allViews,
    selectedView,
    isLoadingViews,
    setUrlViewId,
    setShowNoView,
    showNoView,
    urlViewId,
  } = useSavedViews(isEnabled);

  const [filters, setFilters] = useURLState<NoticeFilters | undefined>(
    "noticeFilters",
    DEFAULT_FILTERS,
    (val) =>
      parseUrlState(
        val as NoticeFilters,
        selectedView &&
          convertViewToNoticeFilters({
            savedView: selectedView,
            signalSettings: signals,
          }),
        signals,
      ),
    { encoder: "compress" },
  );

  // Report pages generate search links with buyer id filters.
  // These are no longer supported by the search page, so we need to fetch
  // the buyers by id to convert them to buyer GUID filters.
  // Unfortunately converting reports to use buyer GUIDS is signficantly harder than
  // this small hack which will only affect the one case of links from reports,
  // as no other part of code uses buyer id filters.
  //
  // TODO: Remove this once we get rid of buyer id filters passed in from the report pages
  const fetchBuyerIdentifiers = useFetchBuyerIdentifiers();
  useEffect(() => {
    if (!filters?.buyerIds || filters.buyerIds.length == 0) {
      return;
    }
    const buyerIds = filters.buyerIds;
    // Run this async - there is unfortunately a small risk of first fetching
    // the incorrect data and then doing the right search once the filters are updated.
    void (async () => {
      const buyers = await fetchBuyerIdentifiers({ ids: buyerIds });

      setFilters(
        (oldFilters) =>
          oldFilters && {
            ...oldFilters,
            buyerIds: undefined,
            buyers: buyers.map((b) => b.guid),
          },
      );
    })();
  }, [fetchBuyerIdentifiers, filters?.buyerIds, setFilters]);

  const [urlTableColumns, setUrlTableColumns] = useURLState<NoticeColumns[] | undefined>(
    "noticeTableColumns",
    undefined,
    (val) => z.array(NoticeColumnsSchema).optional().parse(val),
  );

  let tableColumns;
  if (urlTableColumns) {
    tableColumns = urlTableColumns.map((field) => ({ field }));
  } else if (
    selectedView?.view.tableSettings?.columns &&
    selectedView?.view.tableSettings.columns.length !== 0
  ) {
    tableColumns = selectedView.view.tableSettings.columns;
  } else {
    tableColumns = DEFAULT_NOTICE_COLUMNS;
  }

  const setSelectedView = (viewId?: string, viewName?: string, context?: string | undefined) => {
    if (!viewId) {
      setShowNoView(true);
      setUrlViewId(undefined);
      setFilters(EMPTY_FILTERS);
      setUrlTableColumns(undefined);
      return;
    }

    setUrlViewId(viewId);
    setFilters(undefined);
    setShowNoView(undefined);
    setUrlTableColumns(undefined);
    viewId &&
      logEvent(
        EventNames.viewSelected,
        createSavedViewSelectedEvent("notice", viewId, viewName, context),
      );
  };

  const clearViewAndFilters = () => {
    setShowNoView(true);
    setUrlViewId("");
    // clear all filters except text
    setFilters({ ...EMPTY_FILTERS, text: filters?.text || "" });
    setUrlTableColumns(undefined);
  };

  const viewAsNoticeFilter =
    selectedView &&
    convertViewToNoticeFilters({
      savedView: selectedView,
      signalSettings: signals,
    });

  const setFiltersWrapper = (newFilters: React.SetStateAction<NoticeFilters | undefined>) => {
    // Only set showNoView to undefined if a view was explicitly selected via URL
    // This prevents the selectedView from falling back to recentView/defaultView
    // when the user adds filters without explicitly selecting a view
    if (urlViewId) {
      setShowNoView(undefined);
    }
    setFilters((prev) => {
      const filters = typeof newFilters === "function" ? newFilters(prev) : newFilters;

      return replaceStaticItemsWithTokens(filters, signals);
    });
  };

  const filterValue = filters || viewAsNoticeFilter || DEFAULT_FILTERS;

  const hasUserModifiedFilters = !isEqual(filterValue, viewAsNoticeFilter);

  const hasUserModifiedTableSettings = !isEqual(
    tableColumns,
    selectedView?.view.tableSettings?.columns,
  );

  // When no view is selected, calculate filterCount using getAppliedFilters to ensure
  // consistency with QuickFilterBar which uses the same function to determine applied filters
  const filterCount = selectedView
    ? getAppliedFilterCount(filterValue)
    : getAppliedFilters(filters || EMPTY_FILTERS).length;

  const hasUserModifiedView =
    // We don't want to stop users from leaving the page if they're on empty state with no changes
    showNoView && filterCount === 0
      ? false
      : hasUserModifiedFilters || hasUserModifiedTableSettings;

  return {
    pinnedViews,
    allViews,
    selectedView,
    setSelectedView,
    filters: filterValue,
    setFilters: setFiltersWrapper,
    clearViewAndFilters,
    isLoadingViews,
    isLoadingSignals,
    hasUserModifiedView,
    hasUserModifiedFilters,
    hasUserModifiedTableSettings,
    tableColumns,
    setTableColumns: setUrlTableColumns,
    filterCount,
  };
}

export function getAppliedFilterCount(filters: NoticeFilters) {
  return Object.values(filters).reduce<number>((acc, val) => {
    if (Array.isArray(val)) {
      return acc + (val.length > 0 ? 1 : 0);
    }
    if (typeof val === "object") {
      if (
        ("countries" in val && val.countries && val.countries.length > 0) ||
        ("regions" in val && val.regions && val.regions.length > 0)
      ) {
        return acc + 1;
      }

      const countriesIsEmpty = "countries" in val && val.countries && val.countries.length === 0;

      const regionsIsEmpty = "regions" in val && val.regions && val.regions.length === 0;

      const selectedIsEmpty = "selected" in val && val.selected && val.selected.length === 0;

      // if the object has countries, regions and selected, and they're all empty arrays, then don't count it
      if (countriesIsEmpty && regionsIsEmpty && selectedIsEmpty) {
        return acc;
      }

      // is it a date filter with a filter object
      if ("hideNulls" in val && "filter" in val) {
        // check if the filter object has any values or if hideNulls is true
        const shouldAdd =
          Object.values(val.filter || {}).filter((v) => v).length > 0 || val.hideNulls;
        return acc + (shouldAdd ? 1 : 0);
      }
      // is it a date filter without a filter object
      if ("hideNulls" in val || "to" in val || "from" in val) {
        const shouldAdd = Object.values(val).filter((v) => v).length > 0;
        return acc + (shouldAdd ? 1 : 0);
      }

      // is it a sort object so dont count it
      if ("order" in val && "field" in val) {
        return acc;
      }
      return acc + (Object.keys(val).length > 0 ? 1 : 0);
    }
    if (typeof val === "string") {
      return acc + (val.length > 0 ? 1 : 0);
    }
    if (typeof val === "boolean") {
      return acc + 1;
    }
    return acc + (val ? 1 : 0);
  }, 0);
}
